import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Phone, Printer, Globe, ShieldCheck } from "lucide-react";
import RevealNav from "@/components/RevealNav";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

const PRINCIPLES = [
  {
    n: "01",
    title: "Owners first.",
    body: "Every decision starts with one question: does this put more money and control in the owner's hands? Not more features. Not a bigger cut for us.",
  },
  {
    n: "02",
    title: "Zero compromise on reliability.",
    body: "A dropped ticket or a missed booking isn't a minor bug — it's real revenue and a real relationship lost. We build to the standard of the busiest hour of your week, not the startup.",
  },
  {
    n: "03",
    title: "AI that sounds human.",
    body: "We don't wire up a generic chatbot and call it intelligence. The ordrs voice host is tuned on real customer-facing language — it handles specials, edge cases, and hesitant callers without sounding robotic.",
  },
  {
    n: "04",
    title: "Transparent pricing, forever.",
    body: "You'll never wake up to a changed commission rate or a surprise integration fee. What you see on the pricing page is what your accountant sees.",
  },
];

const STACK = [
  { icon: Globe, label: "Storefronts", desc: "AI-generated, fully branded, on your domain" },
  { icon: Phone, label: "AI Phone", desc: "Personalized host — sub-800ms, 8 languages" },
  { icon: Printer, label: "Direct printing", desc: "PrintNode thermal to your line, zero latency" },
  { icon: Zap, label: "Live orders & bookings", desc: "Idempotent pipeline — no duplicates, ever" },
  { icon: ShieldCheck, label: "Stripe Connect", desc: "Funds split and deposited directly to you" },
];

export default function About() {
  return (
    <div>
      <RevealNav />

      {/* ── HERO ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.pexels.com/photos/6205791/pexels-photo-6205791.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
            alt=""
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/85 to-background" />
        </div>
        <div className="ds-container pt-32 pb-24 lg:pt-40 lg:pb-32">
          <motion.div {...fade}>
            <p className="overline mb-6">about ordrs</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl mb-6 max-w-5xl lg:whitespace-nowrap">
              Built for the business
              <span className="font-script text-primary text-6xl sm:text-7xl lg:text-[8rem] font-normal leading-[0.5] block -mt-2 whitespace-normal">
                that never closes.
              </span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
              ordrs started with a simple observation: independent merchants and service providers alike —
              restaurants, retail shops, salons, clinics, studios, florists — were being crushed by platform
              fees and held hostage by software that wasn't built for them. We built the exit ramp.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── ORIGIN ── */}
      <section className="ds-container py-28 lg:py-36">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <motion.div className="lg:col-span-6" {...fade}>
            <p className="overline mb-4">the origin</p>
            <h2 className="text-4xl lg:text-5xl mb-6">
              The problem was obvious.<br />
              <span className="font-script text-primary text-5xl lg:text-6xl font-normal leading-[0.8] block mt-3">Nobody was fixing it.</span>
            </h2>
            <div className="space-y-5 text-muted-foreground leading-relaxed">
              <p>
                Third-party marketplaces and booking apps charge 15–30% per order or appointment and keep the
                customer relationship. Generic POS and scheduling systems charge for every integration and go
                down at the worst possible moment. AI phone solutions built for enterprise get resold to small
                businesses at enterprise prices.
              </p>
              <p>
                The independent operator — the family taqueria, the neighborhood bakery, the boutique
                retail shop, the solo esthetician, the four-chair barbershop — had no real option. They were
                renting access to their own customers.
              </p>
              <p>
                ordrs is built to end that. One platform: your storefront, your phone line, your
                receptionist, your printer, your calendar, your customer data that stays with you.
              </p>
            </div>
          </motion.div>
          <motion.div className="lg:col-span-6" {...fade} transition={{ duration: 0.6, delay: 0.15 }}>
            <Card className="p-10 bg-foreground text-background overflow-hidden">
              <p className="overline text-primary mb-6">the numbers we're fighting</p>
              <div className="space-y-6">
                <div className="border-b border-background/10 pb-6">
                  <div className="text-5xl font-extrabold text-background mb-1">30%</div>
                  <div className="text-background/60 text-sm">average commission taken by third-party marketplace and booking platforms per order or appointment</div>
                </div>
                <div className="border-b border-background/10 pb-6">
                  <div className="text-5xl font-extrabold text-background mb-1">1%</div>
                  <div className="text-background/60 text-sm">or a flat $1.50 — your choice of fee model, a fraction of what marketplaces take</div>
                </div>
                <div>
                  <div className="text-5xl font-extrabold text-background mb-1">5 min</div>
                  <div className="text-background/60 text-sm">from blank form to live branded storefront with AI phone and order or booking routing active</div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* ── PRINCIPLES ── */}
      <section className="bg-foreground text-background">
        <div className="ds-container py-28 lg:py-36">
          <motion.div {...fade}>
            <p className="overline mb-4 text-primary">how we build</p>
            <h2 className="text-4xl lg:text-5xl mb-16 text-background">
              Four things we won't<br />
              <span className="font-script text-primary text-5xl lg:text-6xl font-normal leading-[0.5] block -mt-2">compromise on.</span>
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            {PRINCIPLES.map((p, i) => (
              <motion.div key={p.n} {...fade} transition={{ duration: 0.5, delay: 0.08 * i }}>
                <Card className="p-8 bg-zinc-900 text-background border-zinc-800 h-full">
                  <p className="overline text-primary mb-4">{p.n}</p>
                  <h3 className="text-xl font-bold text-background mb-3">{p.title}</h3>
                  <p className="text-sm text-background/70 leading-relaxed">{p.body}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PLATFORM OVERVIEW ── */}
      <section className="ds-container py-28 lg:py-36">
        <motion.div {...fade}>
          <p className="overline mb-4">the platform</p>
          <h2 className="text-4xl lg:text-5xl mb-4">
            Everything wired together.<br />
            <span className="font-script text-primary text-5xl lg:text-6xl font-normal leading-[0.8] block mt-3">One dashboard.</span>
          </h2>
          <p className="text-muted-foreground max-w-xl leading-relaxed mb-16">
            ordrs isn't a catalog builder bolted onto a phone bot. Every channel — web, voice, fulfillment,
            scheduling — runs off the same live data. Change a price once and it's everywhere in seconds.
          </p>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {STACK.map((s, i) => (
            <motion.div key={s.label} {...fade} transition={{ duration: 0.5, delay: 0.07 * i }}>
              <Card className="p-8 h-full">
                <s.icon className="w-5 h-5 text-primary mb-4" />
                <h3 className="text-lg font-bold mb-1">{s.label}</h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </Card>
            </motion.div>
          ))}
          <motion.div {...fade} transition={{ duration: 0.5, delay: 0.07 * STACK.length }}>
            <Card className="p-8 h-full border-dashed flex flex-col justify-center items-center text-center">
              <p className="text-sm text-muted-foreground mb-2">And a bunch more.</p>
              <p className="text-xs text-muted-foreground/60">Loyalty, multi-location sync, customer CRM, call transcripts, enterprise compliance — the kitchen sink. All live, none of it "coming soon."</p>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* ── WHO WE SERVE ── */}
      <section className="ds-container py-28 lg:py-36">
        <div className="editorial-divider mb-16" />
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <motion.div className="lg:col-span-5" {...fade}>
            <p className="overline mb-4">who this is for</p>
            <h2 className="text-4xl lg:text-5xl mb-6">
              If you need a
              <span className="font-script text-primary text-5xl lg:text-6xl font-normal leading-[0.8] block mt-3">comprehensive solution,</span>
              we got you.
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              ordrs was designed for the owner who answers their own phone and needs things to
              just work — and it scales cleanly to multi-location groups that need centralized
              control without an enterprise integration project.
            </p>
            <div className="space-y-3 text-sm">
              {[
                "Restaurants and fast-casual operators",
                "Bakeries, delis, and florists",
                "Boutique retail and specialty shops",
                "Salons, spas, and barbershops",
                "Clinics, studios, and appointment-based services",
                "Multi-location and enterprise groups",
                "Ghost kitchens and pop-ups",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div className="lg:col-span-7" {...fade} transition={{ duration: 0.6, delay: 0.15 }}>
            <img
              src="/ai-phone-receptionist.webp"
              alt=""
              className="rounded-lg w-full h-80 lg:h-96 object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="border-t border-border">
        <div className="ds-container py-24 lg:py-32 text-center">
          <motion.div {...fade}>
            <h2 className="text-4xl lg:text-5xl mb-4">
              <span className="font-script text-primary text-5xl lg:text-6xl font-normal block mb-2">See it in five minutes.</span>
              No card required.
            </h2>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              Drop in your website URL and we'll generate a branded storefront with AI phone and order or booking routing active — before you pay a cent.
            </p>
            <Link to="/#preview">
              <Button size="lg" className="h-12 px-8 text-base font-bold group">
                Build my storefront <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
